import moment from "moment";
import {
  SET_CURRENT_COMPANY_BALANCE_SHEET,
  SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT,
  SET_CURRENT_COMPANY_PROFIT_AND_LOSS,
  SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT_SUMMARY,
  SET_CURRENT_RECONCILIATION_OF_VALUATION_METHODS_REPORT,
  SET_CURRENT_GOODWILL_REPORT,
  SET_STOCK_DEAL_SPLIT_REPORT,
  SET_ASSET_SALE_DEAL_SPLIT_REPORT,
  SET_OWNER_FINANCING_STOCK_SALE_REPORT,
  SET_ASSET_SALE_PROCEEDS_WORKSHEET,
  SET_CURRENT_COMPANY_MONTHLY_BALANCE_SHEET,
  SET_CURRENT_COMPANY_MONTHLY_PROFIT_AND_LOSS,
  SET_CURRENT_COMPANY_WORKING_CAPITAL,
  SET_CURRENT_COMPANY_CASH_FLOW,
} from "./mutations";
import FinancialHandler from "../../../handlers/FinancialHandler";
import { store } from "../../index";
import BalanceSheet from "../../../models/valuation/BalanceSheet";
import FinancialAccount from "../../../models/FinancialAccount";
import {
  BalanceSheetReport,
  BalanceSheetReportCategory,
  BalanceSheetReportCategoryGroup,
} from "../../../models/valuation/BalanceSheetReport";

import ReconciliationValuationMethodsReport, {
  ReconciliationValuationMethodsReportGroup,
  ReconciliationValuationMethodsReportGroupItem,
  MinorityShareholderDiscount,
} from "../../../models/valuation/ReconciliationValuationMethodsReport";
import CapitalizationOfEarning from "../../../models/valuation/CapitalizationOfEarning";
import GoodwillReport from "../../../models/transactions/GoodwillReport";
import StockDealSplitReport, {
  StockDealSplitReportItem,
} from "../../../models/transactions/StockDealSplitReport";
import AssetSaleDealSplitReport, {
  AssetSaleDealSplitReportItem,
} from "../../../models/transactions/AssetSaleDealSplitReport";
import {
  SellingExpenses,
  AccountsPayable,
  OtherExpensesNeededToClose,
  CurrentAssetsKeptBySeller,
  LiabilitiesAssumedByBuyer,
  Financing,
  LongTermAssetsKeptBySeller,
  LongTermDebtNotAssumedByBuyer,
} from "../../../models/transactions/OwnerFinancingStockSaleReport";
import OwnerFinancingStockSaleReport from "../../../models/transactions/OwnerFinancingStockSaleReport";
import AssetSaleProceedsWorksheet, {
  AssetAccountsPayable,
  LongTermDebtNotAssumedByPurchaser,
} from "../../../models/transactions/AssetSaleProceedsWorksheet";
import { generateDiscountedCashFlowMethod } from "../discountedCFM/actions";
import CustomField from "../../../models/CustomField";
import MonthlyBalanceSheetReport from "../../../models/financials/MonthlyBalanceSheetReport";
import MonthlyProfitAndLoss from "../../../models/financials/MonthlyProfitAndLoss";
import DateAndValue from "../../../models/DateAndValue";
import WorkingCapital from "../../../models/financials/WorkingCapital";
import CashFlow from "../../../models/financials/CashFlow";
import manualBSReport from "./manualBSReport.json";
import { DESCENDANT_ORDER } from "../../../config";

/************* BALANCE SHEET  *************/
export const updateBalanceSheetItem =
  ({ name, type }, { category }) =>
  (dispatch) => {
    const allowedTypes = ["assets", "liabilities"];
    if(type === "equity") return;
    if (!allowedTypes.includes(type)) throw new Error("Type not accepted");

    const {
      currentCompanyBalanceSheet,
      currentCompanyBalanceSheetReportSummary,
    } = store.getState().financials;
    
    const updatedCurrentCompanyBalanceSheet = new BalanceSheet({
      ...currentCompanyBalanceSheet,
      [type]: currentCompanyBalanceSheet[type].map((asset) => {
        const associatedItem = asset.items.find((item) => {
          return item.name === name;
        });

        if (associatedItem) {
          return new FinancialAccount({
            ...asset,
            items: asset.items.map((assetItem) => {
              if (assetItem.name === associatedItem.name) {
                return new FinancialAccount({
                  ...assetItem,
                  category,
                });
              }

              return assetItem;
            }),
          });
        }

        return asset;
      }),
    });

    const updatedBalanceSheetReport =
      updatedCurrentCompanyBalanceSheet.balanceSheetReport.map((report) => {
        return new BalanceSheetReportCategoryGroup({
          ...report,
          categories: report.categories.map((reportCategory) => {
            const storedCategory = [
              ...store.getState().financials.currentCompanyBalanceSheetReport,
            ]
              .filter((r) => r.groupName === report.groupName)[0]
              .categories.filter(
                (c) => c.categoryValue === reportCategory.categoryValue
              )[0];

            if (
              storedCategory &&
              reportCategory.categoryValue === storedCategory.categoryValue
            ) {
              return new BalanceSheetReportCategory({
                ...reportCategory,
                manuality: storedCategory.manuality,
                analystNote: storedCategory.analystNote,
                analystTool: storedCategory.analystTool,
                manualAdjustedValue: storedCategory.manualAdjustedValue,
                liquidationPercentage: storedCategory.liquidationPercentage,
                excessOrNonOperating: storedCategory.excessOrNonOperating,
              });
            }

            return reportCategory;
          }),
        });
      });
    
    const totalNote = currentCompanyBalanceSheetReportSummary?.totalNote || "";
    const  manuality = currentCompanyBalanceSheetReportSummary?.manuality || "AUTO";
    let updatedCurrentCompanyBalanceSheetReportSummary =
      BalanceSheetReport.calculateSummary(updatedBalanceSheetReport);
    updatedCurrentCompanyBalanceSheetReportSummary = 
      {...updatedCurrentCompanyBalanceSheetReportSummary, totalNote, manuality};

    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT_SUMMARY,
      payload: updatedCurrentCompanyBalanceSheetReportSummary,
    });
    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT,
      payload: updatedBalanceSheetReport,
    });
    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET,
      payload: updatedCurrentCompanyBalanceSheet,
    });
  };

export const fetchCompanyBalanceSheet =
  (
    { companyId },
    { startMonth, endMonth },
  ) =>
  async (dispatch) => {
    const companyManuality = companyId === "manualCompany";
    let balanceSheetReport = manualBSReport;

    if(!companyManuality) { //manual company just passes empty arrays
      const companyBalanceSheets =
        await FinancialHandler.fetchCompanyBalanceSheet(
          { companyId },
          {
            startMonth,
            endMonth,
          }
        );
      
      balanceSheetReport = companyBalanceSheets[0].balanceSheetReport;
    }

    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT,
      payload: balanceSheetReport,
    });
    // dispatch({
    //   type: SET_CURRENT_COMPANY_BALANCE_SHEET,
    //   payload: companyBalanceSheets[0],
    // });

    // return companyBalanceSheets;
  };

export const fetchCompanyMonthlyBalanceSheet =
  (
    { companyId },
    { startMonth, endMonth },
    options = { clearCache: false, dateOrder: "" }
  ) =>
  async (dispatch) => {
    const { currentCompanyMonthlyBalanceSheet } = store.getState().financials;
    const companyManuality = companyId === "manualCompany";

    if (currentCompanyMonthlyBalanceSheet && !options.clearCache) {
      dispatch({
        type: SET_CURRENT_COMPANY_MONTHLY_BALANCE_SHEET,
        payload: new MonthlyBalanceSheetReport({
          dates: currentCompanyMonthlyBalanceSheet.dates,
          assets: currentCompanyMonthlyBalanceSheet.assets.map(
            (asset) =>
              new FinancialAccount({
                ...asset,
                items: asset.items.map(
                  (item) =>
                    new FinancialAccount({
                      ...item,
                    })
                ),
              })
          ),
          liabilities: currentCompanyMonthlyBalanceSheet.liabilities.map(
            (liability) =>
              new FinancialAccount({
                ...liability,
                items: liability.items.map(
                  (item) =>
                    new FinancialAccount({
                      ...item,
                    })
                ),
              })
          ),
          equity: currentCompanyMonthlyBalanceSheet.equity.map(
            (equityItem) =>
              new FinancialAccount({
                ...equityItem,
              })
          ),
        }),
      });

      return;
    }

    const dates = [];
    const assets = [];
    const liabilities = [];
    const equity = [];
    
    if(!companyManuality) { //manual company just passes empty arrays
      const companyBalanceSheets =
        await FinancialHandler.fetchCompanyBalanceSheet(
          { companyId },
          {
            startMonth,
            endMonth,
          }
        );

      companyBalanceSheets?.forEach((balanceSheet) => {
        dates.push(balanceSheet.date);
        const formattedAssets = balanceSheet.assets.map(
          (asset) =>
            new FinancialAccount({
              ...asset,
              date: balanceSheet.date,
              items: asset.items.map(
                (item) =>
                  new FinancialAccount({
                    ...item,
                    date: balanceSheet.date,
                  })
              ),
            })
        );

        const formattedLiabilities = balanceSheet.liabilities.map(
          (liability) =>
            new FinancialAccount({
              ...liability,
              date: balanceSheet.date,
              items: liability.items.map(
                (item) =>
                  new FinancialAccount({
                    ...item,
                    date: balanceSheet.date,
                  })
              ),
            })
        );

        const formattedEquity = balanceSheet.equity.map(
          (equity) =>
            new FinancialAccount({
              ...equity,
              date: balanceSheet.date,
            })
        );

        assets.push(...formattedAssets);
        liabilities.push(...formattedLiabilities);
        equity.push(...formattedEquity);
      });
    }
    else {  //set manual company dates array
      const start = moment.utc(startMonth);
      const end = moment.utc(endMonth);
      for(let i = start; i.isBefore(end); i.add({ months: 1 })) dates.push(i.format("YYYY-MM-01"));
      dates.reverse();
    }

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_BALANCE_SHEET,
      payload: new MonthlyBalanceSheetReport({
        dates,
        assets,
        liabilities,
        equity,
        dateOrder: DESCENDANT_ORDER,
      }),
    });
  };

export const updateMonthlyBalanceSheet =
  ({ name, type, groupName }, { category }) =>
  (dispatch) => {
    const { currentCompanyMonthlyBalanceSheet } = store.getState().financials;

    const _updatedCompanyMonthlyBalanceSheet = {
      ...currentCompanyMonthlyBalanceSheet,
    };
    _updatedCompanyMonthlyBalanceSheet[type] =
      _updatedCompanyMonthlyBalanceSheet[type]?.map((item) => {
        if (type === "equity") {
          if (name === item.name) {
            return new FinancialAccount({
              ...item,
              category,
            });
          }

          return item;
        }

        if (item.name !== groupName) {
          return new FinancialAccount({
            ...item,
            items: item.items.map(
              (groupItem) => new FinancialAccount({ ...groupItem })
            ),
          });
        }

        const _item = new FinancialAccount({
          ...item,
          items: item.items.map((groupItem) => {
            if (groupItem.name === name) {
              return new FinancialAccount({
                ...groupItem,
                category,
              });
            }

            return new FinancialAccount({ ...groupItem });
          }),
        });

        return _item;
      });

    const formatted = new MonthlyBalanceSheetReport({
      ..._updatedCompanyMonthlyBalanceSheet,
      assets: _updatedCompanyMonthlyBalanceSheet.assets.map(
        (asset) =>
          new FinancialAccount({
            ...asset,
          })
      ),
      liabilities: _updatedCompanyMonthlyBalanceSheet.liabilities.map(
        (liability) =>
          new FinancialAccount({
            ...liability,
          })
      ),
      equity: _updatedCompanyMonthlyBalanceSheet.equity.map(
        (equityItem) =>
          new FinancialAccount({
            ...equityItem,
          })
      ),
    });

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_BALANCE_SHEET,
      payload: formatted,
    });
  };

export const updateMonthlyProfitAndLoss =
  ({ name, type }, { category }) =>
  (dispatch) => {
    const { currentCompanyMonthlyProfitAndLoss } = store.getState().financials;

    const _currentCompanyMonthlyProfitAndLoss = {
      ...currentCompanyMonthlyProfitAndLoss,
    };
    _currentCompanyMonthlyProfitAndLoss[type] =
      _currentCompanyMonthlyProfitAndLoss[type]?.map((item) => {
        if (name === item.name) {
          return new FinancialAccount({
            ...item,
            category,
          });
        }

        return item;
      });

    const formatted = new MonthlyProfitAndLoss({
      ...currentCompanyMonthlyProfitAndLoss,
      incomes: _currentCompanyMonthlyProfitAndLoss.incomes.map(
        (income) =>
          new FinancialAccount({
            ...income,
          })
      ),
      expenses: _currentCompanyMonthlyProfitAndLoss.expenses.map(
        (expense) =>
          new FinancialAccount({
            ...expense,
          })
      ),
      otherIncomes: _currentCompanyMonthlyProfitAndLoss.otherIncomes.map(
        (otherIncome) =>
          new FinancialAccount({
            ...otherIncome,
          })
      ),
      otherExpenses: _currentCompanyMonthlyProfitAndLoss.otherExpenses.map(
        (otherExpense) =>
          new FinancialAccount({
            ...otherExpense,
          })
      ),
      costOfSales: _currentCompanyMonthlyProfitAndLoss.costOfSales.map(
        (costOfSale) =>
          new FinancialAccount({
            ...costOfSale,
          })
      ),
      netOperatingProfits:
        _currentCompanyMonthlyProfitAndLoss.netOperatingProfits.map(
          (netOperatingProfit) =>
            new DateAndValue({
              ...netOperatingProfit,
            })
        ),
      netOtherIncomes: _currentCompanyMonthlyProfitAndLoss.netOtherIncomes.map(
        (netOtherIncome) =>
          new DateAndValue({
            ...netOtherIncome,
          })
      ),
      netProfits: _currentCompanyMonthlyProfitAndLoss.netProfits.map(
        (netProfit) =>
          new DateAndValue({
            ...netProfit,
          })
      ),
      grossProfits: _currentCompanyMonthlyProfitAndLoss.grossProfits.map(
        (grossProfit) =>
          new DateAndValue({
            ...grossProfit,
          })
      ),
    });

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_PROFIT_AND_LOSS,
      payload: formatted,
    });
  };

// export const fetchCompanyProfitAndLoss =
//   ({ companyId }, { startMonth, endMonth }) =>
//   async (dispatch) => {
//     const companyProfitAndLossReports =
//     await FinancialHandler.fetchCompanyProfitAndLoss(
//       { companyId },
//       {
//         startMonth,
//         endMonth,
//       }
//     );

//     dispatch({
//       type: SET_CURRENT_COMPANY_PROFIT_AND_LOSS,
//       payload: companyProfitAndLossReports[0],
//     });

//     return companyProfitAndLossReports;
//   };

export const fetchCompanyMonthlyProfitAndLoss =
  (
    { companyId },
    { startMonth, endMonth },
    options = { clearCache: false, dateOrder: "" }
  ) =>
  async (dispatch) => {
    const { currentCompanyMonthlyProfitAndLoss } = store.getState().financials;
    const companyManuality = companyId === "manualCompany";

    if (currentCompanyMonthlyProfitAndLoss && !options.clearCache) {
      dispatch({
        type: SET_CURRENT_COMPANY_MONTHLY_PROFIT_AND_LOSS,
        payload: new MonthlyProfitAndLoss({
          ...currentCompanyMonthlyProfitAndLoss,
          incomes: currentCompanyMonthlyProfitAndLoss.incomes.map(
            (income) =>
              new FinancialAccount({
                ...income,
              })
          ),
          expenses: currentCompanyMonthlyProfitAndLoss.expenses.map(
            (expense) =>
              new FinancialAccount({
                ...expense,
              })
          ),
          otherIncomes: currentCompanyMonthlyProfitAndLoss.otherIncomes.map(
            (otherIncome) =>
              new FinancialAccount({
                ...otherIncome,
              })
          ),
          otherExpenses: currentCompanyMonthlyProfitAndLoss.otherExpenses.map(
            (otherExpense) =>
              new FinancialAccount({
                ...otherExpense,
              })
          ),
          costOfSales: currentCompanyMonthlyProfitAndLoss.costOfSales.map(
            (costOfSale) =>
              new FinancialAccount({
                ...costOfSale,
              })
          ),
          netOperatingProfits:
            currentCompanyMonthlyProfitAndLoss.netOperatingProfits.map(
              (netOperatingProfit) =>
                new DateAndValue({
                  ...netOperatingProfit,
                })
            ),
          netOtherIncomes:
            currentCompanyMonthlyProfitAndLoss.netOtherIncomes.map(
              (netOtherIncome) =>
                new DateAndValue({
                  ...netOtherIncome,
                })
            ),
          netProfits: currentCompanyMonthlyProfitAndLoss.netProfits.map(
            (netProfit) =>
              new DateAndValue({
                ...netProfit,
              })
          ),
          grossProfits: currentCompanyMonthlyProfitAndLoss.grossProfits.map(
            (grossProfit) =>
              new DateAndValue({
                ...grossProfit,
              })
          ),
          dateOrder: options.dateOrder,
        }),
      });

      return;
    }

    const _currentCompanyMonthlyProfitAndLoss = new MonthlyProfitAndLoss({});

    if(!companyManuality) { //manual company just passes empty arrays
      const companyProfitAndLoss =
        await FinancialHandler.fetchCompanyProfitAndLoss(
          { companyId },
          {
            startMonth,
            endMonth,
          }
        );

      companyProfitAndLoss?.forEach((profitAndLoss) => {
        const date = profitAndLoss.fromDateReport;
        _currentCompanyMonthlyProfitAndLoss.dates.push(date);

        const incomes = profitAndLoss.income.items.map(
          (item) =>
            new FinancialAccount({
              ...item,
              date,
            })
        );
        _currentCompanyMonthlyProfitAndLoss.incomes.push(...incomes);

        const expenses = profitAndLoss.expenses.items.map(
          (item) =>
            new FinancialAccount({
              ...item,
              date,
            })
        );
        _currentCompanyMonthlyProfitAndLoss.expenses.push(...expenses);

        const otherIncomes = profitAndLoss.otherIncome.items.map(
          (item) =>
            new FinancialAccount({
              ...item,
              date,
            })
        );
        _currentCompanyMonthlyProfitAndLoss.otherIncomes.push(...otherIncomes);

        const otherExpenses = profitAndLoss.otherExpenses.items.map(
          (item) =>
            new FinancialAccount({
              ...item,
              date,
            })
        );
        _currentCompanyMonthlyProfitAndLoss.otherExpenses.push(...otherExpenses);

        const costOfSales = profitAndLoss.costOfSales.items.map(
          (item) =>
            new FinancialAccount({
              ...item,
              date,
            })
        );
        _currentCompanyMonthlyProfitAndLoss.costOfSales.push(...costOfSales);

        const netOperatingProfit = new DateAndValue({
          date,
          value: profitAndLoss.netOperatingProfit,
        });
        _currentCompanyMonthlyProfitAndLoss.netOperatingProfits.push(
          netOperatingProfit
        );

        const netOtherIncome = new DateAndValue({
          date,
          value: profitAndLoss.netOtherIncomes,
        });
        _currentCompanyMonthlyProfitAndLoss.netOtherIncomes.push(netOtherIncome);

        const netProfit = new DateAndValue({
          date,
          value: profitAndLoss.netProfit,
        });
        _currentCompanyMonthlyProfitAndLoss.netProfits.push(netProfit);

        const grossProfit = new DateAndValue({
          date,
          value: profitAndLoss.grossProfit,
        });
        _currentCompanyMonthlyProfitAndLoss.grossProfits.push(grossProfit);
      });
    }
    else {  //set manual company dates array
      const dates = [];

      const start = moment.utc(startMonth);
      const end = moment.utc(endMonth);
      for(let i = start; i.isBefore(end); i.add({ months: 1 })) dates.push(i.format("YYYY-MM-01"));
      _currentCompanyMonthlyProfitAndLoss.dates = dates.reverse();
    }

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_PROFIT_AND_LOSS,
      payload: new MonthlyProfitAndLoss({
        ..._currentCompanyMonthlyProfitAndLoss,
        incomes: _currentCompanyMonthlyProfitAndLoss.incomes.map(
          (income) =>
            new FinancialAccount({
              ...income,
            })
        ),
        expenses: _currentCompanyMonthlyProfitAndLoss.expenses.map(
          (expense) =>
            new FinancialAccount({
              ...expense,
            })
        ),
        otherIncomes: _currentCompanyMonthlyProfitAndLoss.otherIncomes.map(
          (otherIncome) =>
            new FinancialAccount({
              ...otherIncome,
            })
        ),
        otherExpenses: _currentCompanyMonthlyProfitAndLoss.otherExpenses.map(
          (otherExpense) =>
            new FinancialAccount({
              ...otherExpense,
            })
        ),
        costOfSales: _currentCompanyMonthlyProfitAndLoss.costOfSales.map(
          (costOfSale) =>
            new FinancialAccount({
              ...costOfSale,
            })
        ),
        netOperatingProfits:
          _currentCompanyMonthlyProfitAndLoss.netOperatingProfits.map(
            (netOperatingProfit) =>
              new DateAndValue({
                ...netOperatingProfit,
              })
          ),
        netOtherIncomes:
          _currentCompanyMonthlyProfitAndLoss.netOtherIncomes.map(
            (netOtherIncome) =>
              new DateAndValue({
                ...netOtherIncome,
              })
          ),
        netProfits: _currentCompanyMonthlyProfitAndLoss.netProfits.map(
          (netProfit) =>
            new DateAndValue({
              ...netProfit,
            })
        ),
        grossProfits: _currentCompanyMonthlyProfitAndLoss.grossProfits.map(
          (grossProfit) =>
            new DateAndValue({
              ...grossProfit,
            })
        ),
        dateOrder: options.dateOrder,
      }),
    });
  };

export const updateBalanceSheetReport =
  (
    { groupName, categoryName },
    {
      analystTool,
      manualAdjustedValue,
      liquidationPercentage,
      excessOrNonOperating,
      analystNote,
      totalNote,
      manuality
    }
  ) =>
  (dispatch) => {
    const {
      currentCompanyBalanceSheetReport,
      currentCompanyBalanceSheetReportSummary,
    } = store.getState().financials;

    let updatedCurrentCompanyBalanceSheetReport, updatedCurrentCompanyBalanceSheetReportSummary;

    if(groupName === "TotalNote") {
      updatedCurrentCompanyBalanceSheetReport = currentCompanyBalanceSheetReport;
      updatedCurrentCompanyBalanceSheetReportSummary = {...currentCompanyBalanceSheetReportSummary, totalNote};
    }
    else {
      //manuality update affects all manuality property of every balanceSheetItems
      updatedCurrentCompanyBalanceSheetReport =
        currentCompanyBalanceSheetReport?.map((balanceSheetReport) => {
          //we want to loop all elements if groupName is "Manuality", otherwise, update the specific category
          if (groupName !== "Manuality" && balanceSheetReport.groupName !== groupName)
            return balanceSheetReport;

          return new BalanceSheetReportCategoryGroup({
            ...balanceSheetReport,
            categories: balanceSheetReport.categories.map((category) => {
              if (groupName !== "Manuality" && category.categoryName !== categoryName) return category;

              return new BalanceSheetReportCategory({
                ...category,
                manuality: groupName === "Manuality" ? manuality : category.manuality,
                analystTool:
                  analystTool || analystTool === 0 || analystTool === ""
                    ? analystTool
                    : category.analystTool,
                manualAdjustedValue:
                  manualAdjustedValue ||
                  manualAdjustedValue === 0 ||
                  manualAdjustedValue === ""
                    ? Number(manualAdjustedValue.replace(/,/g, ""))
                    : category.manualAdjustedValue,
                liquidationPercentage:
                  liquidationPercentage ||
                  liquidationPercentage === 0 ||
                  liquidationPercentage === ""
                    ? liquidationPercentage
                    : category.liquidationPercentage,
                excessOrNonOperating:
                  excessOrNonOperating ||
                  excessOrNonOperating === 0 ||
                  excessOrNonOperating === ""
                    ? excessOrNonOperating
                    : category.excessOrNonOperating,
                analystNote:
                  analystNote || analystNote === 0 || analystNote === ""
                    ? analystNote
                    : category.analystNote,
              });
            }),
          });
        });

      if(groupName === "Manuality")
        //total manuality update
        updatedCurrentCompanyBalanceSheetReportSummary = {...currentCompanyBalanceSheetReportSummary, manuality};
      else {
        //specific values update
        totalNote = currentCompanyBalanceSheetReportSummary?.totalNote || "";
        manuality = currentCompanyBalanceSheetReportSummary?.manuality || "AUTO";
        updatedCurrentCompanyBalanceSheetReportSummary =
          BalanceSheetReport.calculateSummary(updatedCurrentCompanyBalanceSheetReport);
        updatedCurrentCompanyBalanceSheetReportSummary =
          {...updatedCurrentCompanyBalanceSheetReportSummary, totalNote, manuality};
      }
    }

    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT_SUMMARY,
      payload: updatedCurrentCompanyBalanceSheetReportSummary,
    });
    dispatch({
      type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT,
      payload: updatedCurrentCompanyBalanceSheetReport,
    });
  };

export const generateCurrentCompanyBalanceSheetReport = () => (dispatch) => {
  const {
    currentCompanyBalanceSheetReport,
    currentCompanyBalanceSheetReportSummary,
  } = store.getState().financials;

  const _currentCompanyBalanceSheetReport = [
    ...currentCompanyBalanceSheetReport,
  ].map((report) => {
    return new BalanceSheetReportCategoryGroup({
      ...report,
      categories: report.categories.map(
        (category) =>
          new BalanceSheetReportCategory({
            ...category,
          })
      ),
    });
  });

  let _currentCompanyBalanceSheetReportSummary =
    BalanceSheetReport.calculateSummary(_currentCompanyBalanceSheetReport);

  const totalNote = currentCompanyBalanceSheetReportSummary?.totalNote || "";
  const manuality = currentCompanyBalanceSheetReportSummary?.manuality || "AUTO";
  _currentCompanyBalanceSheetReportSummary = 
    {..._currentCompanyBalanceSheetReportSummary, totalNote, manuality};

  dispatch({
    type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT_SUMMARY,
    payload: _currentCompanyBalanceSheetReportSummary,
  });
  dispatch({
    type: SET_CURRENT_COMPANY_BALANCE_SHEET_REPORT,
    payload: _currentCompanyBalanceSheetReport,
  });
};

// export const setCurrentCompanyBalanceSheet = () => (dispatch) => {
//   const storedCurrentCompanyBalanceSheet = {
//     ...store.getState().financials.currentCompanyBalanceSheet,
//   };
//   const currentCompanyBalanceSheet = new BalanceSheet({
//     ...storedCurrentCompanyBalanceSheet,
//     assets: storedCurrentCompanyBalanceSheet?.assets?.map(
//       (balanceSheetAssetItem) =>
//         new FinancialAccount({
//           accountId: balanceSheetAssetItem.accountId,
//           name: balanceSheetAssetItem.name,
//           value: balanceSheetAssetItem.value,
//           items: balanceSheetAssetItem.items.map(
//             (subAssetItem) =>
//               new FinancialAccount({
//                 accountId: subAssetItem.accountId,
//                 parentAccountId: balanceSheetAssetItem.accountId,
//                 name: subAssetItem.name,
//                 value: subAssetItem.value,
//                 items: [],
//                 category: subAssetItem.category,
//               })
//           ),
//         })
//     ),
//     liabilities: storedCurrentCompanyBalanceSheet?.liabilities?.map(
//       (balanceSheetLiability) =>
//         new FinancialAccount({
//           accountId: balanceSheetLiability.accountId,
//           name: balanceSheetLiability.name,
//           value: balanceSheetLiability.value,
//           items: balanceSheetLiability.items.map(
//             (subAssetItem) =>
//               new FinancialAccount({
//                 accountId: subAssetItem.accountId,
//                 parentAccountId: balanceSheetLiability.accountId,
//                 name: subAssetItem.name,
//                 value: subAssetItem.value,
//                 items: [],
//                 category: subAssetItem.category,
//               })
//           ),
//         })
//     ),
//     equity: storedCurrentCompanyBalanceSheet?.equity?.map(
//       (balanceSheetEquity) =>
//         new FinancialAccount({
//           accountId: balanceSheetEquity.accountId,
//           name: balanceSheetEquity.name,
//           value: balanceSheetEquity.value,
//           items: balanceSheetEquity.items.map(
//             (subAssetItem) =>
//               new FinancialAccount({
//                 accountId: subAssetItem.accountId,
//                 parentAccountId: balanceSheetEquity.accountId,
//                 name: subAssetItem.name,
//                 value: subAssetItem.value,
//                 items: [],
//                 category: subAssetItem.category,
//               })
//           ),
//         })
//     ),
//     earliestAvailableMonth:
//       storedCurrentCompanyBalanceSheet.earliestAvailableMonth,
//     mostRecentAvailableMonth:
//       storedCurrentCompanyBalanceSheet.mostRecentAvailableMonth,
//     totalAssetsAmount: storedCurrentCompanyBalanceSheet?.assets?.value,
//     totalEquityAmount: storedCurrentCompanyBalanceSheet?.equity?.value,
//     totalLiabilitiesAmount:
//       storedCurrentCompanyBalanceSheet?.liabilities?.values,
//     netAssetsAmount: storedCurrentCompanyBalanceSheet?.netAssetsAmount,
//   });

//   dispatch({
//     type: SET_CURRENT_COMPANY_BALANCE_SHEET,
//     payload: currentCompanyBalanceSheet,
//   });
// };

/************* RECONCILIATION OF VALUATION METHODS  *************/

export const setReconciliationOfValuationMethodsReport = () => (dispatch) => {
  const { currentReconciliationOfValuationMethodsReport } =
    store.getState().financials;

  if (!currentReconciliationOfValuationMethodsReport) return;

  const reconciliationOfValuationMethodsReport =
    new ReconciliationValuationMethodsReport({
      ...currentReconciliationOfValuationMethodsReport,
      reportGroups:
        currentReconciliationOfValuationMethodsReport.reportGroups.map(
          (reportGroup) => {
            return new ReconciliationValuationMethodsReportGroup({
              ...reportGroup,
              groupItems: reportGroup.groupItems.map((groupItem) => {
                return new ReconciliationValuationMethodsReportGroupItem({
                  ...groupItem,
                });
              }),
            });
          }
        ),
      minorityShareholderDiscount: new MinorityShareholderDiscount({
        ...currentReconciliationOfValuationMethodsReport?.minorityShareholderDiscount,
      }),
    });

  dispatch({
    type: SET_CURRENT_RECONCILIATION_OF_VALUATION_METHODS_REPORT,
    payload: reconciliationOfValuationMethodsReport,
  });
};

export const generateReconciliationOfValuationMethodsReport =
  () => (dispatch) => {
    dispatch(generateCurrentCompanyBalanceSheetReport());
    dispatch(generateDiscountedCashFlowMethod());
    const { currentCompanyBalanceSheetReportSummary } =
      store.getState().financials;
    const { currentReconciliationOfValuationMethodsReport } =
      store.getState().financials;

    const capitalizationOfEarningsData =
      store.getState().capitalizationOfEarnings?.capitalizationOfEarnings;
    const discountedCFM =
      store.getState().discountedCFM?.discountedCashFlowMethod;

    const totalLiquidationValue =
      currentCompanyBalanceSheetReportSummary.equity.totalLiquidationValue;

    const capitalizationOfEarnings = new CapitalizationOfEarning({
      afterTaxFreeCashFlow: capitalizationOfEarningsData?.afterTaxFreeCashFlow,
      afterTaxNetCashFlowCapitalizationRate:
        capitalizationOfEarningsData?.afterTaxNetCashFlowCapitalizationRate,
      afterTaxCapitalizedEarningsOfSubjectCompany:
        capitalizationOfEarningsData?.afterTaxCapitalizedEarningsOfSubjectCompany,
      beforeTaxCapitalizedEarningsOfSubjectCompany:
        capitalizationOfEarningsData?.beforeTaxCapitalizedEarningsOfSubjectCompany,
      preTaxFreeCashFlow: capitalizationOfEarningsData?.preTaxFreeCashFlow,
      preTaxNetCashFlowCapitalizationRate:
        capitalizationOfEarningsData?.preTaxNetCashFlowCapitalizationRate,
      preTaxIncomeCapitalizedEarningsOfSubjectCompany:
        capitalizationOfEarningsData?.preTaxCapitalizedEarningsOfSubjectCompany,
      ebit: capitalizationOfEarningsData?.ebit,
      preTaxNetIncomeCapitalizationRate:
        capitalizationOfEarningsData?.preTaxNetIncomeCapitalizationRate,
    });

    const totalExcessAssetsOrLiabilities =
      currentCompanyBalanceSheetReportSummary?.equity?.totalExcessOrNonOperating;

    const reconciliationOfValuationMethodsReport =
      ReconciliationValuationMethodsReport.generateReport({
        totalLiquidationValue,
        totalEquityBalance:
          currentCompanyBalanceSheetReportSummary?.equity?.totalBalance,
        totalEquityAdjustedValue:
          currentCompanyBalanceSheetReportSummary?.equity?.totalAdjustedValue,
        totalLiabilitiesAdjustedValue:
          currentCompanyBalanceSheetReportSummary?.liabilities?.totalAdjustedValue,
        afterTaxCapitalizedEarningsOfSubjectCompany:
          capitalizationOfEarnings?.afterTaxCapitalizedEarningsOfSubjectCompany,
        valueOfSubjectCompany: discountedCFM.valueSubjectCompany,
        totalExcessAssetsOrLiabilities,
        reconciliationOfValuationMethodsReport:
          currentReconciliationOfValuationMethodsReport,
      });

    dispatch({
      type: SET_CURRENT_RECONCILIATION_OF_VALUATION_METHODS_REPORT,
      payload: reconciliationOfValuationMethodsReport,
    });
  };

export const updateReconciliationOfValuationMethodsReport =
  (updatedReport) => (dispatch) => {
    dispatch({
      type: SET_CURRENT_RECONCILIATION_OF_VALUATION_METHODS_REPORT,
      payload: updatedReport,
    });
  };

/************* GOODWILL REPORT  *************/

export const generateGoodwillReport = () => (dispatch) => {
  const { offerPriceValue } = store.getState().financingCalculator;
  const balanceSheetReportSummary =
    store.getState().financials.currentCompanyBalanceSheetReportSummary;

  const goodwillAmount =
    offerPriceValue - balanceSheetReportSummary?.assets?.totalAdjustedValue;
  const totalAssets =
    goodwillAmount + balanceSheetReportSummary?.assets?.totalAdjustedValue;
  const totalLiabilities =
    balanceSheetReportSummary?.liabilities?.totalAdjustedValue;
  const equityValueOfBusiness = totalAssets - totalLiabilities;

  const goodwillReport = new GoodwillReport({
    goodwillAmount,
    goodwillPercentageOfAssets:
      offerPriceValue === 0 ? 0 : (goodwillAmount / totalAssets) * 100,
    goodwillPercentageOfEquity: (goodwillAmount / equityValueOfBusiness) * 100,
    totalAssets,
    totalLiabilities,
  });

  dispatch({ type: SET_CURRENT_GOODWILL_REPORT, payload: goodwillReport });
};

/************* STOCK DEAL SPLIT REPORT  *************/

export const generateStockDealSplitReport = () => (dispatch) => {
  dispatch(generateCurrentCompanyBalanceSheetReport());

  const {
    currentAssets,
    longTermAssets,
    currentLiabilities,
    longTermLiabilities,
  } = FinancialAccount.getAllowedBalanceSheetItemGroups();

  const { currentCompanyBalanceSheetReport, stockDealSplitReport } =
    store.getState().financials;

  const { BUYER_STOCK_DEAL_OWNER } =
    StockDealSplitReportItem.getAllowedStockDealOwnerValues();

  dispatch({
    type: SET_STOCK_DEAL_SPLIT_REPORT,
    payload: new StockDealSplitReport({
      currentAssetsSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === currentAssets;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory = stockDealSplitReport?.currentAssetsSplit.find(
            (reportItem) => reportItem.value === category.categoryValue
          );

          return new StockDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            stockDealOwner: storedCategory
              ? storedCategory.stockDealOwner
              : BUYER_STOCK_DEAL_OWNER,
          });
        }),
      longTermAssetsSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === longTermAssets;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory = stockDealSplitReport?.longTermAssetsSplit.find(
            (reportItem) => reportItem.value === category.categoryValue
          );

          return new StockDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            stockDealOwner: storedCategory
              ? storedCategory.stockDealOwner
              : BUYER_STOCK_DEAL_OWNER,
          });
        }),
      currentLiabilitiesSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === currentLiabilities;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            stockDealSplitReport?.currentLiabilitiesSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new StockDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            stockDealOwner: storedCategory
              ? storedCategory.stockDealOwner
              : BUYER_STOCK_DEAL_OWNER,
          });
        }),
      longTermLiabilitiesSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === longTermLiabilities;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            stockDealSplitReport?.longTermLiabilitiesSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new StockDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            stockDealOwner: storedCategory
              ? storedCategory.stockDealOwner
              : BUYER_STOCK_DEAL_OWNER,
          });
        }),
    }),
  });
};

export const updateStockDealSplitReport =
  ({ stockDealSplitReportKey }, { stockDealOwner, stockDealItemValue }) =>
  (dispatch) => {
    const storedStockDealReport = new StockDealSplitReport({
      ...store.getState().financials.stockDealSplitReport,
    });

    const _stockDealReport = new StockDealSplitReport({
      currentAssetsSplit: storedStockDealReport?.currentAssetsSplit.map(
        (reportItem) =>
          new StockDealSplitReportItem({
            ...reportItem,
          })
      ),
      longTermAssetsSplit: storedStockDealReport?.longTermAssetsSplit.map(
        (reportItem) =>
          new StockDealSplitReportItem({
            ...reportItem,
          })
      ),
      currentLiabilitiesSplit:
        storedStockDealReport?.currentLiabilitiesSplit.map(
          (reportItem) =>
            new StockDealSplitReportItem({
              ...reportItem,
            })
        ),
      longTermLiabilitiesSplit:
        storedStockDealReport?.longTermLiabilitiesSplit.map(
          (reportItem) =>
            new StockDealSplitReportItem({
              ...reportItem,
            })
        ),
    });

    _stockDealReport[stockDealSplitReportKey] = _stockDealReport[
      stockDealSplitReportKey
    ].map((reportItem) => {
      if (reportItem.value === stockDealItemValue) {
        return new StockDealSplitReportItem({
          ...reportItem,
          stockDealOwner,
        });
      }

      return reportItem;
    });

    dispatch({ type: SET_STOCK_DEAL_SPLIT_REPORT, payload: _stockDealReport });
  };

/************* ASSET DEAL SPLIT REPORT  *************/

export const generateAssetSaleDealSplitReport = () => (dispatch) => {
  dispatch(generateCurrentCompanyBalanceSheetReport());
  const {
    currentAssets,
    longTermAssets,
    currentLiabilities,
    longTermLiabilities,
  } = FinancialAccount.getAllowedBalanceSheetItemGroups();

  const { currentCompanyBalanceSheetReport, assetSaleDealSplitReport } =
    store.getState().financials;

  const { BUYER_ASSET_SALE_DEAL_OWNER } =
    AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues();

  dispatch({
    type: SET_ASSET_SALE_DEAL_SPLIT_REPORT,
    payload: new AssetSaleDealSplitReport({
      currentAssetsSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === currentAssets;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            assetSaleDealSplitReport?.currentAssetsSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new AssetSaleDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            assetSaleDealOwner: storedCategory
              ? storedCategory.assetSaleDealOwner
              : BUYER_ASSET_SALE_DEAL_OWNER,
          });
        }),
      longTermAssetsSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === longTermAssets;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            assetSaleDealSplitReport?.longTermAssetsSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new AssetSaleDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            assetSaleDealOwner: storedCategory
              ? storedCategory.assetSaleDealOwner
              : BUYER_ASSET_SALE_DEAL_OWNER,
          });
        }),
      currentLiabilitiesSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === currentLiabilities;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            assetSaleDealSplitReport?.currentLiabilitiesSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new AssetSaleDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            assetSaleDealOwner: storedCategory
              ? storedCategory.assetSaleDealOwner
              : BUYER_ASSET_SALE_DEAL_OWNER,
          });
        }),
      longTermLiabilitiesSplit: currentCompanyBalanceSheetReport
        ?.filter((report) => {
          return report.groupName === longTermLiabilities;
        })[0]
        ?.categories?.map((category) => {
          const storedCategory =
            assetSaleDealSplitReport?.longTermLiabilitiesSplit.find(
              (reportItem) => reportItem.value === category.categoryValue
            );

          return new AssetSaleDealSplitReportItem({
            name: category.categoryName,
            value: category.categoryValue,
            amount: category.adjustedValue,
            assetSaleDealOwner: storedCategory
              ? storedCategory.assetSaleDealOwner
              : BUYER_ASSET_SALE_DEAL_OWNER,
          });
        }),
    }),
  });
};

export const updateAssetSaleDealSplitReport =
  ({ splitReportKey }, { assetSaleDealOwner, assetSaleDealItemValue }) =>
  (dispatch) => {
    const storedAssetSaleDealReport = new AssetSaleDealSplitReport({
      ...store.getState().financials.assetSaleDealSplitReport,
    });

    const _assetSaleDealReport = new AssetSaleDealSplitReport({
      currentAssetsSplit: storedAssetSaleDealReport?.currentAssetsSplit.map(
        (reportItem) =>
          new AssetSaleDealSplitReportItem({
            ...reportItem,
          })
      ),
      longTermAssetsSplit: storedAssetSaleDealReport?.longTermAssetsSplit.map(
        (reportItem) =>
          new AssetSaleDealSplitReportItem({
            ...reportItem,
          })
      ),
      currentLiabilitiesSplit:
        storedAssetSaleDealReport?.currentLiabilitiesSplit.map(
          (reportItem) =>
            new AssetSaleDealSplitReportItem({
              ...reportItem,
            })
        ),
      longTermLiabilitiesSplit:
        storedAssetSaleDealReport?.longTermLiabilitiesSplit.map(
          (reportItem) =>
            new AssetSaleDealSplitReportItem({
              ...reportItem,
            })
        ),
    });

    _assetSaleDealReport[splitReportKey] = _assetSaleDealReport[
      splitReportKey
    ].map((reportItem) => {
      if (reportItem.value === assetSaleDealItemValue) {
        return new AssetSaleDealSplitReportItem({
          ...reportItem,
          assetSaleDealOwner,
        });
      }

      return reportItem;
    });

    dispatch({
      type: SET_ASSET_SALE_DEAL_SPLIT_REPORT,
      payload: _assetSaleDealReport,
    });
  };

/******************** OWNER FINANCING STOCK SALE REPORT **********************/
export const generateOwnerFinancingStockSaleReport = () => (dispatch) => {
  dispatch(generateGoodwillReport());
  dispatch(generateStockDealSplitReport());

  const {
    stockDealSplitReport,
    currentGoodwillReport,
    ownerFinancingStockSaleReport,
    currentCompanyBalanceSheetReportSummary,
  } = store.getState().financials;
  const { downPaymentPercentage, offerPriceValue, totalInterest } =
    store.getState().financingCalculator;

  const equalsAmountBuyerPaysSellerInCashAndNotes =
    stockDealSplitReport?.getEqualsAmountBuyerPaysSellerInCashAndNotes(
      {
        businessGoodwill: currentGoodwillReport.goodwillAmount,
      },
      { format: false }
    );

  const cashDownPayment =
    equalsAmountBuyerPaysSellerInCashAndNotes * (downPaymentPercentage / 100);

  const { SELLER_STOCK_DEAL_OWNER, BUYER_STOCK_DEAL_OWNER } =
    StockDealSplitReportItem.getAllowedStockDealOwnerValues();

  const longTermDebtsNotAssumedByBuyer =
    stockDealSplitReport?.longTermLiabilitiesSplit?.filter(
      (longTermLiability) => {
        return (
          longTermLiability.stockDealOwner === SELLER_STOCK_DEAL_OWNER
        );
      }
    ) || [];
    
  const currentAssetsKeptBySeller =
    stockDealSplitReport?.currentAssetsSplit?.filter((currentAsset) => {
      return currentAsset.stockDealOwner === SELLER_STOCK_DEAL_OWNER;
    });

  const currentLiabilitiesKeptByBuyer =
    stockDealSplitReport?.currentLiabilitiesSplit.filter((liability) => {
      return liability.stockDealOwner === BUYER_STOCK_DEAL_OWNER;
    }) || [];

  const longTermLiabilitiesKeptByBuyer =
    stockDealSplitReport?.longTermLiabilitiesSplit.filter((liability) => {
      return liability.stockDealOwner === BUYER_STOCK_DEAL_OWNER;
    }) || [];

  const longTermAssetsKeptBySeller =
    stockDealSplitReport?.longTermAssetsSplit.filter((asset) => {
      return asset.stockDealOwner === SELLER_STOCK_DEAL_OWNER;
    }) || [];

  const liabilitiesKeptByBuyer = [
    ...currentLiabilitiesKeptByBuyer,
    ...longTermLiabilitiesKeptByBuyer,
  ];

  const excessBalanceSheetAssets =
    currentCompanyBalanceSheetReportSummary?.assets?.totalExcessOrNonOperating +
    currentCompanyBalanceSheetReportSummary?.liabilities?.totalExcessOrNonOperating;

  const _ownerFinancingStockSaleReport = new OwnerFinancingStockSaleReport({
    cashDownPayment,
    sellingExpenses: new SellingExpenses({
      ...ownerFinancingStockSaleReport?.sellingExpenses,
    }),
    accountsPayable: new AccountsPayable({
      ...ownerFinancingStockSaleReport?.accountsPayable,
    }),
    longTermDebtNotAssumedByBuyer: new LongTermDebtNotAssumedByBuyer({
      ...ownerFinancingStockSaleReport?.longTermDebtNotAssumedByBuyer,
      longTermDebt: longTermDebtsNotAssumedByBuyer,
    }),
    otherExpensesNeededToClose: new OtherExpensesNeededToClose({
      ...ownerFinancingStockSaleReport?.otherExpensesNeededToClose,
    }),
    totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
      ...ownerFinancingStockSaleReport?.totalCurrentAssetsKeptBySeller,
      currentAssets: currentAssetsKeptBySeller,
    }),
    existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
      ...ownerFinancingStockSaleReport?.existingLiabilitiesAssumedByBuyer,
      liabilitiesAssumedByBuyer: liabilitiesKeptByBuyer,
    }),
    totalSellingPrice: offerPriceValue,
    excessBalanceSheetAssets,
    longTermAssetsKeptBySeller: new LongTermAssetsKeptBySeller({
      ...ownerFinancingStockSaleReport?.longTermAssetsKeptBySeller,
      longTermAssets: longTermAssetsKeptBySeller,
    }),
    financing: new Financing({
      financingPrinciple: 0,
      interestOwed: 0,
    }),
    totalInterest,
    customFieldToAdd: new CustomField({
      ...ownerFinancingStockSaleReport?.customFieldToAdd,
    }),
    customFieldToSubtract: new CustomField({
      ...ownerFinancingStockSaleReport?.customFieldToSubtract,
    }),
  });

  dispatch({
    type: SET_OWNER_FINANCING_STOCK_SALE_REPORT,
    payload: _ownerFinancingStockSaleReport,
  });
};

export const updateOwnerFinancingStockSaleReport =
  ({
    sellingExpenses = {
      brokerageFees: 0,
      filingsAndRecordings: 0,
      attorneyFees: 0,
      accountantFees: 0,
      escrowFees: 0,
    },
    accountsPayable = {
      payrollPayables: 0,
      salesTaxPayables: 0,
    },
    otherExpensesNeededToClose = {
      equipmentRepair: 0,
      equipmentReplace: 0,
      complianceWithGovernmentalAgencies: 0,
    },
    totalCurrentAssetsKeptBySeller = {
      returnOfLeaseDeposit: 0,
      pettyCash: 0,
      proratedRent: 0,
      utilityDeposit: 0,
      proratedTaxes: 0,
      rebateForPrepaidExpenses: 0,
    },
    existingLiabilitiesAssumedByBuyer = {
      customerPrepayments: 0,
      vacationPayable: 0,
    },
    customFieldToAdd,
    customFieldToSubtract,
  }) =>
  (dispatch) => {
    const { ownerFinancingStockSaleReport } = store.getState().financials;
    if (!ownerFinancingStockSaleReport) return;

    if (
      sellingExpenses?.brokerageFees ||
      sellingExpenses?.brokerageFees === ""
    ) {
      ownerFinancingStockSaleReport.sellingExpenses.brokerageFees =
        sellingExpenses?.brokerageFees;
    }
    if (
      sellingExpenses?.filingsAndRecordings ||
      sellingExpenses?.filingsAndRecordings === ""
    ) {
      ownerFinancingStockSaleReport.sellingExpenses.filingsAndRecordings =
        sellingExpenses?.filingsAndRecordings;
    }
    if (sellingExpenses?.attorneyFees || sellingExpenses?.attorneyFees === "") {
      ownerFinancingStockSaleReport.sellingExpenses.attorneyFees =
        sellingExpenses?.attorneyFees;
    }
    if (
      sellingExpenses?.accountantFees ||
      sellingExpenses?.accountantFees === ""
    ) {
      ownerFinancingStockSaleReport.sellingExpenses.accountantFees =
        sellingExpenses?.accountantFees;
    }
    if (sellingExpenses?.escrowFees || sellingExpenses?.escrowFees === "") {
      ownerFinancingStockSaleReport.sellingExpenses.escrowFees =
        sellingExpenses?.escrowFees;
    }

    if (
      accountsPayable?.payrollPayables ||
      accountsPayable?.payrollPayables === ""
    ) {
      ownerFinancingStockSaleReport.accountsPayable.payrollPayables =
        accountsPayable?.payrollPayables;
    }
    if (
      accountsPayable?.salesTaxPayables ||
      accountsPayable?.salesTaxPayables === ""
    ) {
      ownerFinancingStockSaleReport.accountsPayable.salesTaxPayables =
        accountsPayable?.salesTaxPayables;
    }

    if (
      otherExpensesNeededToClose?.equipmentRepair ||
      otherExpensesNeededToClose?.equipmentRepair === ""
    ) {
      ownerFinancingStockSaleReport.otherExpensesNeededToClose.equipmentRepair =
        otherExpensesNeededToClose.equipmentRepair;
    }
    if (
      otherExpensesNeededToClose?.equipmentReplace ||
      otherExpensesNeededToClose?.equipmentReplace === ""
    ) {
      ownerFinancingStockSaleReport.otherExpensesNeededToClose.equipmentReplace =
        otherExpensesNeededToClose.equipmentReplace;
    }
    if (
      otherExpensesNeededToClose?.complianceWithGovernmentalAgencies ||
      otherExpensesNeededToClose?.complianceWithGovernmentalAgencies === ""
    ) {
      ownerFinancingStockSaleReport.otherExpensesNeededToClose.complianceWithGovernmentalAgencies =
        otherExpensesNeededToClose.complianceWithGovernmentalAgencies;
    }

    if (customFieldToAdd) {
      ownerFinancingStockSaleReport.customFieldToAdd = customFieldToAdd;
    }

    if (customFieldToSubtract) {
      ownerFinancingStockSaleReport.customFieldToSubtract =
        customFieldToSubtract;
    }

    const {
      returnOfLeaseDeposit,
      pettyCash,
      proratedRent,
      proratedTaxes,
      rebateForPrepaidExpenses,
      utilityDeposit,
    } = totalCurrentAssetsKeptBySeller;

    if (returnOfLeaseDeposit || returnOfLeaseDeposit === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.returnOfLeaseDeposit =
        returnOfLeaseDeposit;
    }
    if (pettyCash || pettyCash === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.pettyCash =
        pettyCash;
    }
    if (proratedRent || proratedRent === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.proratedRent =
        proratedRent;
    }
    if (proratedTaxes || proratedTaxes === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.proratedTaxes =
        proratedTaxes;
    }
    if (rebateForPrepaidExpenses || rebateForPrepaidExpenses === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.rebateForPrepaidExpenses =
        rebateForPrepaidExpenses;
    }
    if (utilityDeposit || utilityDeposit === "") {
      ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller.utilityDeposit =
        utilityDeposit;
    }

    const { customerPrepayments, vacationPayable } =
      existingLiabilitiesAssumedByBuyer;
    if (customerPrepayments || customerPrepayments === "") {
      ownerFinancingStockSaleReport.existingLiabilitiesAssumedByBuyer.customerPrepayments =
        customerPrepayments;
    }
    if (vacationPayable || vacationPayable === "") {
      ownerFinancingStockSaleReport.existingLiabilitiesAssumedByBuyer.vacationPayable =
        vacationPayable;
    }

    dispatch({
      type: SET_OWNER_FINANCING_STOCK_SALE_REPORT,
      payload: new OwnerFinancingStockSaleReport({
        ...ownerFinancingStockSaleReport,
        sellingExpenses: new SellingExpenses({
          ...ownerFinancingStockSaleReport.sellingExpenses,
        }),
        accountsPayable: new AccountsPayable({
          ...ownerFinancingStockSaleReport.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...ownerFinancingStockSaleReport.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...ownerFinancingStockSaleReport.existingLiabilitiesAssumedByBuyer,
        }),
        customFieldToAdd: new CustomField({
          ...ownerFinancingStockSaleReport?.customFieldToAdd,
        }),
        customFieldToSubtract: new CustomField({
          ...ownerFinancingStockSaleReport?.customFieldToSubtract,
        }),
      }),
    });
  };

export const addCustomFieldToStockSaleProceedsWorksheet =
  ({ section, name, value }) =>
  (dispatch) => {
    const { ownerFinancingStockSaleReport } = store.getState().financials;

    ownerFinancingStockSaleReport[section].customFields = [
      ...ownerFinancingStockSaleReport[section].customFields,
      new CustomField({ name, value }),
    ];

    dispatch({
      type: SET_OWNER_FINANCING_STOCK_SALE_REPORT,
      payload: new OwnerFinancingStockSaleReport({
        ...ownerFinancingStockSaleReport,
        sellingExpenses: new SellingExpenses({
          ...ownerFinancingStockSaleReport.sellingExpenses,
        }),
        accountsPayable: new AccountsPayable({
          ...ownerFinancingStockSaleReport.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...ownerFinancingStockSaleReport.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...ownerFinancingStockSaleReport.existingLiabilitiesAssumedByBuyer,
        }),
      }),
    });
  };

export const updateCustomFieldOnStockSaleProceedsWorksheet =
  ({ section, customFields }) =>
  (dispatch) => {
    const { ownerFinancingStockSaleReport } = store.getState().financials;

    ownerFinancingStockSaleReport[section].customFields = customFields;

    dispatch({
      type: SET_OWNER_FINANCING_STOCK_SALE_REPORT,
      payload: new OwnerFinancingStockSaleReport({
        ...ownerFinancingStockSaleReport,
        sellingExpenses: new SellingExpenses({
          ...ownerFinancingStockSaleReport.sellingExpenses,
        }),
        accountsPayable: new AccountsPayable({
          ...ownerFinancingStockSaleReport.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...ownerFinancingStockSaleReport.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...ownerFinancingStockSaleReport.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...ownerFinancingStockSaleReport.existingLiabilitiesAssumedByBuyer,
        }),
        financing: new Financing({
          ...ownerFinancingStockSaleReport.financing,
        }),
      }),
    });
  };

/******************** OWNER FINANCING ASSET PROCEEDS WORKSHEET **********************/

/************* ASSET SALE PROCEEDS WORKSHEET  *************/

export const generateAssetSaleProceedsWorksheet = () => (dispatch) => {
  dispatch(generateGoodwillReport());
  dispatch(generateAssetSaleDealSplitReport());

  const {
    assetSaleDealSplitReport,
    currentGoodwillReport,
    assetSaleProceedsWorksheet,
    currentCompanyBalanceSheetReportSummary,
  } = store.getState().financials;

  const {
    cashNeeded,
    downPaymentPercentage,
    offerPriceValue,
    totalInterest,
    interestRate,
    monthlyPayments,
  } = store.getState().financingCalculator;

  const equalsAmountBuyerPaysSellerInCashAndNotes =
    assetSaleDealSplitReport?.getEqualsAmountBuyerPaysSellerInCashAndNotes(
      {
        businessGoodwill: currentGoodwillReport.goodwillAmount,
      },
      { format: false }
    );

  // const cashDownPayment = equalsAmountBuyerPaysSellerInCashAndNotes * (downPaymentPercentage / 100);
  const cashDownPayment = cashNeeded?.rows[0]?.value || 0;

  const { SELLER_ASSET_SALE_DEAL_OWNER, BUYER_ASSET_SALE_DEAL_OWNER } =
    AssetSaleDealSplitReportItem.getAllowedAssetDealOwnerValues();

  const currentAssetsKeptBySeller =
    assetSaleDealSplitReport?.currentAssetsSplit?.filter((currentAsset) => {
      return currentAsset.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER;
    }) || [];

  const currentLiabilitiesKeptByBuyer =
    assetSaleDealSplitReport?.currentLiabilitiesSplit.filter((liability) => {
      return liability.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER;
    }) || [];

  const longTermLiabilitiesKeptByBuyer =
    assetSaleDealSplitReport?.longTermLiabilitiesSplit.filter((liability) => {
      return liability.assetSaleDealOwner === BUYER_ASSET_SALE_DEAL_OWNER;
    }) || [];

  const longTermAssetsKeptBySeller =
    assetSaleDealSplitReport?.longTermAssetsSplit.filter((asset) => {
      return asset.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER;
    }) || [];

  const accountsPayableLiabilities =
    assetSaleDealSplitReport?.currentLiabilitiesSplit?.filter(
      (currentLiability) => {
        return (
          currentLiability.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
        );
      }
    ) || [];

  const longTermDebtsNotAssumedByPurchaser =
    assetSaleDealSplitReport?.longTermLiabilitiesSplit?.filter(
      (longTermLiability) => {
        return (
          longTermLiability.assetSaleDealOwner === SELLER_ASSET_SALE_DEAL_OWNER
        );
      }
    ) || [];

  const liabilitiesKeptByBuyer = [
    ...currentLiabilitiesKeptByBuyer,
    ...longTermLiabilitiesKeptByBuyer,
  ];

  const excessBalanceSheetAssets =
    currentCompanyBalanceSheetReportSummary?.assets?.totalExcessOrNonOperating +
    currentCompanyBalanceSheetReportSummary?.liabilities?.totalExcessOrNonOperating;

  const _assetSaleProceedsWorksheet = new AssetSaleProceedsWorksheet({
    cashDownPayment,
    sellingExpenses: new SellingExpenses({
      ...assetSaleProceedsWorksheet?.sellingExpenses,
    }),
    accountsPayable: new AssetAccountsPayable({
      ...assetSaleProceedsWorksheet?.accountsPayable,
      liabilities: accountsPayableLiabilities,
    }),
    longTermDebtsNotAssumedByPurchaser: new LongTermDebtNotAssumedByPurchaser({
      ...assetSaleProceedsWorksheet?.longTermDebtsNotAssumedByPurchaser,
      longTermDebts: longTermDebtsNotAssumedByPurchaser,
    }),
    otherExpensesNeededToClose: new OtherExpensesNeededToClose({
      ...assetSaleProceedsWorksheet?.otherExpensesNeededToClose,
    }),
    totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
      ...assetSaleProceedsWorksheet?.totalCurrentAssetsKeptBySeller,
      currentAssets: currentAssetsKeptBySeller,
    }),
    existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
      ...assetSaleProceedsWorksheet?.existingLiabilitiesAssumedByBuyer,
      liabilitiesAssumedByBuyer: liabilitiesKeptByBuyer,
    }),
    totalSellingPrice: offerPriceValue,
    excessBalanceSheetAssets,
    longTermAssetsKeptBySeller: new LongTermAssetsKeptBySeller({
      ...assetSaleProceedsWorksheet?.longTermAssetsKeptBySeller,
      longTermAssets: longTermAssetsKeptBySeller,
    }),
    totalInterest,
    interestRate,
    monthlyPayments,
    amountToPayAfterCashDownPayment: equalsAmountBuyerPaysSellerInCashAndNotes,
    customFieldToAdd: new CustomField({
      ...assetSaleProceedsWorksheet?.customFieldToAdd,
    }),
    customFieldToSubtract: new CustomField({
      ...assetSaleProceedsWorksheet?.customFieldToSubtract,
    }),
  });

  dispatch({
    type: SET_ASSET_SALE_PROCEEDS_WORKSHEET,
    payload: _assetSaleProceedsWorksheet,
  });
};

export const updateAssetSaleProceedsWorksheet =
  ({
    dates = [],
    sellingExpenses = {
      brokerageFees: 0,
      filingsAndRecordings: 0,
      attorneyFees: 0,
      accountantFees: 0,
      escrowFees: 0,
    },
    accountsPayable = {
      payrollPayables: 0,
      salesTaxPayables: 0,
    },
    otherExpensesNeededToClose = {
      equipmentRepair: 0,
      equipmentReplace: 0,
      complianceWithGovernmentalAgencies: 0,
    },
    totalCurrentAssetsKeptBySeller = {
      returnOfLeaseDeposit: 0,
      pettyCash: 0,
      proratedRent: 0,
      utilityDeposit: 0,
      proratedTaxes: 0,
      rebateForPrepaidExpenses: 0,
    },
    existingLiabilitiesAssumedByBuyer = {
      customerPrepayments: 0,
      vacationPayable: 0,
    },
    customFieldToAdd,
    customFieldToSubtract,
  }) =>
  (dispatch) => {
    const { assetSaleProceedsWorksheet } = store.getState().financials;
    if (!assetSaleProceedsWorksheet) return;

    if (
      sellingExpenses?.brokerageFees ||
      sellingExpenses?.brokerageFees === ""
    ) {
      assetSaleProceedsWorksheet.sellingExpenses.brokerageFees =
        sellingExpenses?.brokerageFees;
    }
    if (
      sellingExpenses?.filingsAndRecordings ||
      sellingExpenses?.filingsAndRecordings === ""
    ) {
      assetSaleProceedsWorksheet.sellingExpenses.filingsAndRecordings =
        sellingExpenses?.filingsAndRecordings;
    }
    if (sellingExpenses?.attorneyFees || sellingExpenses?.attorneyFees === "") {
      assetSaleProceedsWorksheet.sellingExpenses.attorneyFees =
        sellingExpenses?.attorneyFees;
    }
    if (
      sellingExpenses?.accountantFees ||
      sellingExpenses?.accountantFees === ""
    ) {
      assetSaleProceedsWorksheet.sellingExpenses.accountantFees =
        sellingExpenses?.accountantFees;
    }
    if (sellingExpenses?.escrowFees || sellingExpenses?.escrowFees === "") {
      assetSaleProceedsWorksheet.sellingExpenses.escrowFees =
        sellingExpenses?.escrowFees;
    }

    if (
      accountsPayable?.payrollPayables ||
      accountsPayable?.payrollPayables === ""
    ) {
      assetSaleProceedsWorksheet.accountsPayable.payrollPayables =
        accountsPayable?.payrollPayables;
    }
    if (
      accountsPayable?.salesTaxPayables ||
      accountsPayable?.salesTaxPayables === ""
    ) {
      assetSaleProceedsWorksheet.accountsPayable.salesTaxPayables =
        accountsPayable?.salesTaxPayables;
    }

    if (
      otherExpensesNeededToClose?.equipmentRepair ||
      otherExpensesNeededToClose?.equipmentRepair === ""
    ) {
      assetSaleProceedsWorksheet.otherExpensesNeededToClose.equipmentRepair =
        otherExpensesNeededToClose.equipmentRepair;
    }
    if (
      otherExpensesNeededToClose?.equipmentReplace ||
      otherExpensesNeededToClose?.equipmentReplace === ""
    ) {
      assetSaleProceedsWorksheet.otherExpensesNeededToClose.equipmentReplace =
        otherExpensesNeededToClose.equipmentReplace;
    }
    if (
      otherExpensesNeededToClose?.complianceWithGovernmentalAgencies ||
      otherExpensesNeededToClose?.complianceWithGovernmentalAgencies === ""
    ) {
      assetSaleProceedsWorksheet.otherExpensesNeededToClose.complianceWithGovernmentalAgencies =
        otherExpensesNeededToClose.complianceWithGovernmentalAgencies;
    }

    if (customFieldToAdd) {
      assetSaleProceedsWorksheet.customFieldToAdd = customFieldToAdd;
    }

    if (customFieldToSubtract) {
      assetSaleProceedsWorksheet.customFieldToSubtract = customFieldToSubtract;
    }

    const {
      returnOfLeaseDeposit,
      pettyCash,
      proratedRent,
      proratedTaxes,
      rebateForPrepaidExpenses,
      utilityDeposit,
    } = totalCurrentAssetsKeptBySeller;

    if (returnOfLeaseDeposit || returnOfLeaseDeposit === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.returnOfLeaseDeposit =
        returnOfLeaseDeposit;
    }
    if (pettyCash || pettyCash === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.pettyCash =
        pettyCash;
    }
    if (proratedRent || proratedRent === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.proratedRent =
        proratedRent;
    }
    if (proratedTaxes || proratedTaxes === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.proratedTaxes =
        proratedTaxes;
    }
    if (rebateForPrepaidExpenses || rebateForPrepaidExpenses === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.rebateForPrepaidExpenses =
        rebateForPrepaidExpenses;
    }
    if (utilityDeposit || utilityDeposit === "") {
      assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller.utilityDeposit =
        utilityDeposit;
    }

    const { customerPrepayments, vacationPayable } =
      existingLiabilitiesAssumedByBuyer;
    if (customerPrepayments || customerPrepayments === "") {
      assetSaleProceedsWorksheet.existingLiabilitiesAssumedByBuyer.customerPrepayments =
        customerPrepayments;
    }
    if (vacationPayable || vacationPayable === "") {
      assetSaleProceedsWorksheet.existingLiabilitiesAssumedByBuyer.vacationPayable =
        vacationPayable;
    }

    dispatch({
      type: SET_ASSET_SALE_PROCEEDS_WORKSHEET,
      payload: new AssetSaleProceedsWorksheet({
        ...assetSaleProceedsWorksheet,
        sellingExpenses: new SellingExpenses({
          ...assetSaleProceedsWorksheet.sellingExpenses,
        }),
        accountsPayable: new AssetAccountsPayable({
          ...assetSaleProceedsWorksheet.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...assetSaleProceedsWorksheet.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...assetSaleProceedsWorksheet.existingLiabilitiesAssumedByBuyer,
        }),
        longTermDebtsNotAssumedByPurchaser:
          new LongTermDebtNotAssumedByPurchaser({
            ...assetSaleProceedsWorksheet.longTermDebtsNotAssumedByPurchaser,
          }),
        longTermAssetsKeptBySeller: new LongTermAssetsKeptBySeller({
          ...assetSaleProceedsWorksheet.longTermAssetsKeptBySeller,
        }),
        customFieldToAdd: new CustomField({
          ...assetSaleProceedsWorksheet?.customFieldToAdd,
        }),
        customFieldToSubtract: new CustomField({
          ...assetSaleProceedsWorksheet?.customFieldToSubtract,
        }),
      }),
    });
  };

export const addCustomFieldToAssetSaleProceedsWorksheet =
  ({ section, name, value }) =>
  (dispatch) => {
    const { assetSaleProceedsWorksheet } = store.getState().financials;

    assetSaleProceedsWorksheet[section].customFields = [
      ...assetSaleProceedsWorksheet[section].customFields,
      new CustomField({ name, value }),
    ];

    dispatch({
      type: SET_ASSET_SALE_PROCEEDS_WORKSHEET,
      payload: new AssetSaleProceedsWorksheet({
        ...assetSaleProceedsWorksheet,
        sellingExpenses: new SellingExpenses({
          ...assetSaleProceedsWorksheet.sellingExpenses,
        }),
        longTermDebtsNotAssumedByPurchaser:
          new LongTermDebtNotAssumedByPurchaser({
            ...assetSaleProceedsWorksheet.longTermDebtsNotAssumedByPurchaser,
          }),
        accountsPayable: new AssetAccountsPayable({
          ...assetSaleProceedsWorksheet.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...assetSaleProceedsWorksheet.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...assetSaleProceedsWorksheet.existingLiabilitiesAssumedByBuyer,
        }),
      }),
    });
  };

export const updateCustomFieldOnAssetSaleProceedsWorksheet =
  ({ section, customFields }) =>
  (dispatch) => {
    const { assetSaleProceedsWorksheet } = store.getState().financials;

    assetSaleProceedsWorksheet[section].customFields = customFields;

    dispatch({
      type: SET_ASSET_SALE_PROCEEDS_WORKSHEET,
      payload: new AssetSaleProceedsWorksheet({
        ...assetSaleProceedsWorksheet,
        sellingExpenses: new SellingExpenses({
          ...assetSaleProceedsWorksheet.sellingExpenses,
        }),
        accountsPayable: new AssetAccountsPayable({
          ...assetSaleProceedsWorksheet.accountsPayable,
        }),
        otherExpensesNeededToClose: new OtherExpensesNeededToClose({
          ...assetSaleProceedsWorksheet.otherExpensesNeededToClose,
        }),
        totalCurrentAssetsKeptBySeller: new CurrentAssetsKeptBySeller({
          ...assetSaleProceedsWorksheet.totalCurrentAssetsKeptBySeller,
        }),
        existingLiabilitiesAssumedByBuyer: new LiabilitiesAssumedByBuyer({
          ...assetSaleProceedsWorksheet.existingLiabilitiesAssumedByBuyer,
        }),
        financing: new Financing({
          ...assetSaleProceedsWorksheet.financing,
        }),
      }),
    });
  };

/************* GENERATE CASH FLOW  *************/

export const generateCashFlow =
  (options = { dateOrder: DESCENDANT_ORDER }) =>
  async (dispatch) => {
    const { companyId, dataSettings } = store.getState().appState;

    await dispatch(
      fetchCompanyMonthlyBalanceSheet(
        { companyId },
        {
          periodsToCompare: dataSettings.yearsToFetch,
        }
      )
    );

    await dispatch(
      fetchCompanyMonthlyProfitAndLoss(
        { companyId },
        {
          periodsToCompare: dataSettings.yearsToFetch,
        }
      )
    );

    //We generate cash flow statement from balance sheet
    const {
      currentCompanyMonthlyBalanceSheet,
      currentCompanyMonthlyProfitAndLoss,
    } = store.getState().financials;

    //get asset accounts from balance sheet
    const [
      monthlyTotalCash,
      monthlyTotalMarketableSecurities,
      monthlyTotalAccountsReceivable,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
      monthlyTotalRealEstateAssets,
      monthlyTotalOtherLongTermAssets,
      monthlyTotalGrossFixedAssets,
    ] = FinancialAccount.getAssetsCategoryOptions().map((categoryObject) => {
      return currentCompanyMonthlyBalanceSheet.getAssetsCategoryValues(
        categoryObject.value,
        categoryObject.group
      );
    });

    //get liability accounts from balance sheet
    const [
      monthlyTotalAccountsPayable,
      monthlyTotalLoansNotesPayable,
      monthlyTotalOtherCurrentLiabilities,
      monthlyTotalLongTermLiabilities,
      monthlyTotalNonOperatingLongTermLiabilities,
      monthlyTotalOtherLongTermLiabilities,
    ] = FinancialAccount.getLiabilitiesCategoryOptions().map(
      (categoryObject) => {
        return currentCompanyMonthlyBalanceSheet.getLiabilitiesCategoryValues(
          categoryObject.value,
          categoryObject.group
        );
      }
    );

    const monthlyTotalEquity =
      currentCompanyMonthlyBalanceSheet.getTotalEquities();

    const cashFlow = new CashFlow({
      dates: currentCompanyMonthlyBalanceSheet.dates,
      years: currentCompanyMonthlyBalanceSheet.yearsFormatted,
      netProfit: currentCompanyMonthlyProfitAndLoss.totalAfterTaxNetProfits,
      monthlydepreciationAmortization:
        currentCompanyMonthlyProfitAndLoss.totalDepreciationDepletionAndAmortization,
      monthlyTotalAccountsReceivable,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
      monthlyTotalAccountsPayable,
      monthlyTotalOtherCurrentLiabilities,
      monthlyTotalGrossFixedAssets,
      monthlyTotalRealEstateAssets,
      monthlyTotalOtherLongTermAssets,
      monthlyTotalLongTermLiabilities,
      monthlyTotalLoansNotesPayable,
      monthlyTotalNonOperatingLongTermLiabilities,
      monthlyTotalOtherLongTermLiabilities,
      monthlyTotalMarketableSecurities,
      monthlyTotalEquity,
      monthlyTotalCash,
      dateOrder: options.dateOrder,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_CASH_FLOW,
      payload: cashFlow,
    });
  };

/************* WORKING CAPITAL  *************/
export const generateWorkingCapital =
  (options = { dateOrder: DESCENDANT_ORDER }) =>
  async (dispatch) => {
    const { companyId, dataSettings } = store.getState().appState;

    await dispatch(
      fetchCompanyMonthlyBalanceSheet(
        { companyId },
        {
          periodsToCompare: dataSettings.yearsToFetch,
        }
      )
    );

    await dispatch(
      fetchCompanyMonthlyProfitAndLoss(
        { companyId },
        {
          periodsToCompare: dataSettings.yearsToFetch,
        }
      )
    );

    const {
      currentCompanyMonthlyBalanceSheet,
      currentCompanyMonthlyProfitAndLoss,
    } = store.getState().financials;

    //get asset accounts from balance sheet
    const [
      monthlyTotalCash,
      monthlyTotalMarketableSecurities,
      monthlyTotalAccountsReceivable,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
      monthlyTotalRealEstateAssets,
      monthlyTotalOtherLongTermAssets,
      monthlyTotalGrossFixedAssets,
    ] = FinancialAccount.getAssetsCategoryOptions().map((categoryObject) => {
      return currentCompanyMonthlyBalanceSheet.getAssetsCategoryValues(
        categoryObject.value,
        categoryObject.group
      );
    });

    //get liability accounts from balance sheet
    const [
      monthlyTotalAccountsPayable,
      monthlyTotalLoansNotesPayable,
      monthlyTotalOtherCurrentLiabilities,
      monthlyTotalLongTermLiabilities,
      monthlyTotalNonOperatingLongTermLiabilities,
      monthlyTotalOtherLongTermLiabilities,
    ] = FinancialAccount.getLiabilitiesCategoryOptions().map(
      (categoryObject) => {
        return currentCompanyMonthlyBalanceSheet.getLiabilitiesCategoryValues(
          categoryObject.value,
          categoryObject.group
        );
      }
    );

    const workingCapital = new WorkingCapital({
      dates: currentCompanyMonthlyProfitAndLoss.dates,
      years: currentCompanyMonthlyBalanceSheet.yearsFormatted,
      monthlyTotalCash,
      monthlyTotalAccountsReceivable,
      monthlyTotalSales: currentCompanyMonthlyProfitAndLoss.totalSales,
      monthlyTotalAccountsPayable,
      monthlyTotalCostOfSales:
        currentCompanyMonthlyProfitAndLoss.totalCostOfSales,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
      monthlyTotalOtherCurrentLiabilities,
      monthlyTotalMarketableSecurities,
      dateOrder: options.dateOrder,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_WORKING_CAPITAL,
      payload: workingCapital,
    });
  };

export const updateFinancialsReportDateOrder =
  (dateOrder) => async (dispatch) => {
    const {
      currentCompanyMonthlyBalanceSheet,
      currentCompanyMonthlyProfitAndLoss,
      currentCompanyWorkingCapital,
      currentCompanyCashFlow,
    } = store.getState().financials;

    const _currentMonthlyBalanceSheet = new MonthlyBalanceSheetReport({
      ...currentCompanyMonthlyBalanceSheet,
      assets: currentCompanyMonthlyBalanceSheet.assets.map(
        (asset) =>
          new FinancialAccount({
            ...asset,
          })
      ),
      liabilities: currentCompanyMonthlyBalanceSheet.liabilities.map(
        (liability) =>
          new FinancialAccount({
            ...liability,
          })
      ),
      equity: currentCompanyMonthlyBalanceSheet.equity.map(
        (equityItem) =>
          new FinancialAccount({
            ...equityItem,
          })
      ),
      dateOrder,
    });

    const _currentCompanyMonthlyProfitAndLoss = new MonthlyProfitAndLoss({
      ...currentCompanyMonthlyProfitAndLoss,
      incomes: currentCompanyMonthlyProfitAndLoss.incomes.map(
        (income) =>
          new FinancialAccount({
            ...income,
          })
      ),
      expenses: currentCompanyMonthlyProfitAndLoss.expenses.map(
        (expense) =>
          new FinancialAccount({
            ...expense,
          })
      ),
      otherIncomes: currentCompanyMonthlyProfitAndLoss.otherIncomes.map(
        (otherIncome) =>
          new FinancialAccount({
            ...otherIncome,
          })
      ),
      otherExpenses: currentCompanyMonthlyProfitAndLoss.otherExpenses.map(
        (otherExpense) =>
          new FinancialAccount({
            ...otherExpense,
          })
      ),
      costOfSales: currentCompanyMonthlyProfitAndLoss.costOfSales.map(
        (costOfSale) =>
          new FinancialAccount({
            ...costOfSale,
          })
      ),
      netOperatingProfits:
        currentCompanyMonthlyProfitAndLoss.netOperatingProfits.map(
          (netOperatingProfit) =>
            new DateAndValue({
              ...netOperatingProfit,
            })
        ),
      netOtherIncomes: currentCompanyMonthlyProfitAndLoss.netOtherIncomes.map(
        (netOtherIncome) =>
          new DateAndValue({
            ...netOtherIncome,
          })
      ),
      netProfits: currentCompanyMonthlyProfitAndLoss.netProfits.map(
        (netProfit) =>
          new DateAndValue({
            ...netProfit,
          })
      ),
      grossProfits: currentCompanyMonthlyProfitAndLoss.grossProfits.map(
        (grossProfit) =>
          new DateAndValue({
            ...grossProfit,
          })
      ),
      dateOrder,
    });

    const _currentCompanyWorkingCapital = new WorkingCapital({
      ...currentCompanyWorkingCapital,
      dateOrder,
    });

    const _currentCompanyCashFlow = new CashFlow({
      ...currentCompanyCashFlow,
      dateOrder,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_BALANCE_SHEET,
      payload: _currentMonthlyBalanceSheet,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_MONTHLY_PROFIT_AND_LOSS,
      payload: _currentCompanyMonthlyProfitAndLoss,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_WORKING_CAPITAL,
      payload: _currentCompanyWorkingCapital,
    });

    dispatch({
      type: SET_CURRENT_COMPANY_CASH_FLOW,
      payload: _currentCompanyCashFlow,
    });
  };
